'use client';

import React, { useState, useRef } from 'react';
import Image from 'next/image';
import { HelpCircle, Rocket, Users, Zap } from 'lucide-react';
import config from '@/config';
import { cn } from '@/src/libs/utils';

type Feature = {
  title: string;
  description: string;
  type?: 'video' | 'image';
  path?: string;
  format?: string;
  alt?: string;
  svg?: React.ReactNode;
  customContent?: React.ReactNode;
  className?: string;
};

const features = [
  {
    title: 'Question types',
    description: `We have 10 questions types to support a variety of quizzing formats:
          multiple choice, multiple response, true/false, numerical answer,
          short text answer, drag and drop into text, ordering, open-ended,
          random question and audio answer.`,
    svg: <HelpCircle size={22} />,
    type: 'image',
    path: `${config.imagesBaseUrl}/landing/Question types2.png`,
    alt: 'Question types',
  },
  {
    title: 'Power-ups',
    description:
      'Make the race even more interesting with unique powerups, visually watch the effects on the game screen!',
    alt: 'Powerup question mark',
    svg: <Zap size={22} />,
    customContent: (
      <div className="relative group w-full aspect-square sm:w-[26rem]">
        <div className="absolute -inset-1 bg-gradient-to-r from-sky-500 to-violet-500 rounded-2xl blur-sm opacity-50 transition duration-1000 group-hover:opacity-75 group-hover:duration-200" />
        <div
          className={`bg-white relative rounded-2xl flex justify-center items-center z-10 h-full bg-cover bg-[url('../../public/powerups-bg.png')] bg-center`}
        >
          <Image
            src={`${config.imagesBaseUrl}/powerups/Q-mark-512px.gif`}
            alt="Powerup question mark"
            width={80}
            height={80}
          />
          <Image
            src={`${config.imagesBaseUrl}/powerups/full cannon.png`}
            alt="Cannon powerup"
            width={120}
            height={120}
            className="scale-x-[-1] absolute bottom-1 right-4"
          />
          <Image
            src={`${config.imagesBaseUrl}/powerups/swap.png`}
            alt="Swap powerup"
            width={90}
            height={90}
            className="absolute top-2 right-3 lg:top-[55px] lg:right-[48px]"
          />
          <Image
            src={`${config.imagesBaseUrl}/powerups/speed boost.png`}
            alt="Energy blast powerup"
            width={100}
            height={100}
            className="absolute bottom-[40px] left-2 lg:left-[20px] -rotate-12"
          />
          <Image
            src={`${config.imagesBaseUrl}/powerups/freeze.png`}
            alt="Freeze powerup"
            width={100}
            height={100}
            className="absolute top-1 lg:top-[30px] left-2 lg:left-[30px]"
          />
        </div>
      </div>
    ),
  },
  {
    title: '100 Player Capacity',
    description: `Get ready to host epic quiz battles with a capacity of up to 100
          players! QuizWhizzer is designed to accommodate large groups, making
          it perfect for classrooms, corporate training, or large-scale online
          events.`,
    svg: <Users size={22} />,
    type: 'image',
    alt: '100 players in a game',
    path: `${config.imagesBaseUrl}/landing/100 players.png`,
  },
  {
    title: 'Custom Avatars',
    description:
      'Add your own avatars to players to customize your game further and increase engagement.',
    svg: <Rocket size={22} />,
    type: 'image',
    path: `${config.imagesBaseUrl}/landing/custom avatars.png`,
  },
] as Feature[];

const Item = ({
  feature,
  isOpen,
  setFeatureSelected,
}: {
  index: number;
  feature: Feature;
  isOpen: boolean;
  setFeatureSelected: () => void;
}) => {
  const accordion = useRef<HTMLDivElement>(null);
  const { title, description, svg } = feature;

  return (
    <li>
      <button
        className="relative flex gap-3 items-center w-full py-5 font-display text-lg md:text-xl text-left border-b border-ink-900/15"
        onClick={(e) => {
          e.preventDefault();
          setFeatureSelected();
        }}
        aria-expanded={isOpen}
      >
        <span
          className={`w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
            isOpen
              ? 'bg-brand-500 text-white'
              : 'bg-paper-200 text-ink-600'
          }`}
        >
          {svg}
        </span>
        <span
          className={`flex-1 ${
            isOpen ? 'text-brand-600 font-bold' : 'text-ink-900 font-semibold'
          }`}
        >
          <h3 className="inline">{title}</h3>
        </span>
        <svg
          className={`w-5 h-5 text-ink-400 transition-transform duration-200 ${
            isOpen ? 'rotate-180' : ''
          }`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      <div
        ref={accordion}
        className="transition-all duration-300 ease-in-out overflow-hidden"
        style={
          isOpen
            ? { maxHeight: accordion?.current?.scrollHeight, opacity: 1 }
            : { maxHeight: 0, opacity: 0 }
        }
      >
        <div className="py-4 text-ink-600 leading-relaxed">{description}</div>
      </div>
    </li>
  );
};

const Media = ({ feature }: { feature: Feature }) => {
  const { type, path, format, alt, className } = feature;
  const style = 'rounded-2xl aspect-square w-full sm:w-[26rem]';
  const size = {
    width: 500,
    height: 500,
  };

  if (feature.customContent) {
    return feature.customContent;
  }

  if (type === 'video') {
    return (
      <video
        className={cn(style, className)}
        autoPlay
        muted
        loop
        playsInline
        controls
        width={size.width}
        height={size.height}
      >
        <source src={path} type={format} />
      </video>
    );
  } else if (type === 'image' && path) {
    return (
      <Image
        src={path}
        alt={alt ?? ''}
        className={cn(style, 'object-cover object-center shadow-lg border border-slate-100', className)}
        width={size.width}
        height={size.height}
      />
    );
  } else {
    return <div className={`${style} !border-none`}></div>;
  }
};

export default function FeaturesAccordion() {
  const [featureSelected, setFeatureSelected] = useState<number>(0);

  return (
    <section className="bg-paper-50 paper-grain" id="features">
      <div className="py-20 lg:py-28 max-w-7xl mx-auto">
        <div className="px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <p className="font-hand text-brand-600 text-2xl mb-3 -rotate-1 inline-block">
              the toolkit
            </p>
            <h2 className="font-display font-black text-4xl md:text-5xl lg:text-6xl tracking-[-0.02em] text-ink-900 leading-[1.02]">
              Everything you need
              <br />
              to make{' '}
              <span className="italic font-normal">
                <span className="ink-underline">learning fun.</span>
              </span>
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
            <ul className="w-full">
              {features.map((feature, i) => (
                <Item
                  key={feature.title}
                  index={i}
                  feature={feature}
                  isOpen={featureSelected === i}
                  setFeatureSelected={() => setFeatureSelected(i)}
                />
              ))}
            </ul>

            <div className="flex justify-center lg:sticky lg:top-24">
              <Media
                feature={features[featureSelected]}
                key={featureSelected}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
